/* STUB DATA — see src/stubs/README.md. Deleted whole when a server is there. */

import type {
  AccountSession,
  AgentUsage,
  AnonymousBudget,
  CertificateReport,
  ComponentVersion,
  IdentityChoice,
  PeopleUsage,
  ReconcileReport,
  ReconcileStep,
  RenderedSettings,
  Resource,
  ResourceUsage,
  Scope,
  RoleReading,
  SessionUsage,
  SettingsState,
  TenancyIdentity,
  TenancyRef,
  Tenant,
  TrailEntry,
} from '../api/types';
import { STUB_TENANCY_DOMAIN } from './stubEntry';

/** Every domain below is under the reserved `example` namespace. Nothing real. */
export const STUB_PASSWORD = 'demo-password';

export const STUB_ACCOUNTS = {
  admin: 'operator@provider.example',
  support: 'support@provider.example',
  tenancy: 'kate@acme.example',
} as const;

/**
 * Whether this demonstration pretends the deployment runs an identity provider
 * of its own.
 *
 * A deployment that runs none refuses `kind: "provider"` at the edge, and the
 * console has to show the option unavailable rather than let somebody pick a
 * cluster nobody can sign in to. Both halves of that are worth being able to
 * see, so it is a switch — see src/stubs/README.md.
 */
export const STUB_PROVIDER_ISSUER = 'https://id.provider.example';

export const STUB_TENANCY: TenancyRef = {
  slug: 'acme',
  display_name: 'Acme Corp',
  tenancy_domain: STUB_TENANCY_DOMAIN,
  console_domain: STUB_TENANCY_DOMAIN,
  gateway_domain: `proxy.${STUB_TENANCY_DOMAIN}`,
};

const HOUR = 3_600_000;

function inHours(hours: number): string {
  return new Date(Date.now() + hours * HOUR).toISOString();
}

/**
 * The three roles the control plane has, and the capabilities each carries.
 *
 * Copied from the server rather than derived, because that is what the server
 * sends: a provider operator does everything except choose a customer's
 * identity provider, the support desk reads, and a customer's representative
 * runs their own tenancy.
 *
 * None of the three carries a catalogue. Writing one is done in a cluster
 * console by an administrator of that cluster, and no route in the account
 * does it for them.
 */
export const STUB_SESSIONS: Record<string, AccountSession> = {
  [STUB_ACCOUNTS.admin]: {
    actor: {
      email: STUB_ACCOUNTS.admin,
      display_name: 'Dana Okafor',
      audit_name: STUB_ACCOUNTS.admin,
    },
    role: 'provider_admin',
    capabilities: [
      'view_tenant',
      'create_tenant',
      'reconcile_tenant',
      'issue_join_token',
      // Which version a fleet runs is what the provider answers for, and
      // signing a wave off decides when everybody else moves. Neither the
      // support desk nor a customer's representative carries this.
      'roll_out_version',
      'delete_tenant',
    ],
    expires_at: inHours(12),
  },
  [STUB_ACCOUNTS.support]: {
    actor: {
      email: STUB_ACCOUNTS.support,
      display_name: 'Sam Reyes',
      audit_name: STUB_ACCOUNTS.support,
    },
    role: 'provider_support',
    capabilities: ['view_tenant'],
    expires_at: inHours(12),
  },
  [STUB_ACCOUNTS.tenancy]: {
    actor: {
      email: STUB_ACCOUNTS.tenancy,
      display_name: 'Kate Ilyina',
      audit_name: STUB_ACCOUNTS.tenancy,
    },
    role: 'tenant_admin',
    capabilities: ['view_tenant', 'issue_join_token', 'choose_identity_provider'],
    tenancy: STUB_TENANCY,
    expires_at: inHours(12),
  },
};

/**
 * The tenancy has not had a provider applied yet — the state a tenancy is in
 * right after its cluster is built, and the one the choice screen exists for.
 */
/**
 * The demonstration starts on a tenancy that is **working**: a provider applied,
 * people able to sign in to its console.
 *
 * It used to start before that — declared and not yet applied — so that the
 * chain "choose a provider, watch the console come alive" could be played out.
 * The cost was that the cluster console opened on a page with no way in, which
 * is the first thing anybody looks at and the last thing that should need
 * explaining. The chain is still there to play: change the provider in the
 * account and this tenancy goes back through every state it has.
 */
export const STUB_IDENTITY: TenancyIdentity = {
  tenancy: STUB_TENANCY,
  state: 'applied',
  in_flight: false,
  desired: { kind: 'provider', issuer: '', client_id: '', audience: '', signing_algs: [] },
  applied: { kind: 'provider', issuer: '', client_id: '', audience: '', signing_algs: [] },
  revertable: false,
  redirect_uri: `https://${STUB_TENANCY_DOMAIN}/login`,
  provider_available: true,
  provider_issuer: STUB_PROVIDER_ISSUER,
  detail: 'the cluster is signing people in through it',
  changed_at: new Date(Date.now() - 4 * HOUR).toISOString(),
};

/** Whoever the tenancy's identity provider vouches for in the demonstration. */
export const STUB_CLUSTER_ACTOR = {
  subject: 'ivan.petrov@acme.example',
  display_name: 'Ivan Petrov',
  groups: ['acme-admins', 'platform'],
};

/**
 * The same person, named by a provider that names people its own way.
 *
 * A subject with colons in it is not something gdsgate will write to an audit
 * trail, so a write made under it is refused at the edge with
 * `operator_not_nameable` and nothing changes on either side. It is a real
 * shape — a broker in front of another directory produces exactly this — and
 * worth being able to see rather than read about.
 */
export const STUB_UNNAMEABLE_SUBJECT = 'f:71c1e:ivan';

/**
 * The administrator group Acme's cluster was seeded with. Fixed at declaration.
 *
 * Named after the tenancy, which is the shape the contract prescribes: the
 * `gdsgate-` namespace belongs to the identities a cluster issues itself, and a
 * tenancy declared inside it is one nobody signs in to. A demonstration must not
 * show a name the server refuses — see `screens/account/adminGroup.ts`.
 */
export const STUB_ADMIN_GROUP = 'acme-admins';

export const STUB_TENANTS: Tenant[] = [
  {
    slug: 'acme',
    display_name: 'Acme Corp',
    tenancy_domain: STUB_TENANCY_DOMAIN,
    console_domain: STUB_TENANCY_DOMAIN,
    gateway_domain: `proxy.${STUB_TENANCY_DOMAIN}`,
    admin_group: STUB_ADMIN_GROUP,
    state: 'ready',
    version: 'v0.7.0',
    transport_ca_fingerprint: 'A1:B2:C3:D4:E5:F6:07:18:29:3A:4B:5C:6D:7E:8F:90',
    generation: 4,
    applied_generation: 4,
    up_to_date: true,
    created_at: '2026-07-26T09:12:00Z',
    updated_at: '2026-07-29T09:12:00Z',
  },
  {
    slug: 'northwind',
    display_name: 'Northwind Logistics',
    tenancy_domain: 'northwind.gate.example',
    console_domain: 'northwind.gate.example',
    gateway_domain: 'proxy.northwind.gate.example',
    admin_group: 'platform',
    state: 'ready',
    version: 'v0.7.0',
    transport_ca_fingerprint: '5E:44:1C:9B:2D:70:AA:31:64:8C:0F:D2:19:E7:35:B8',
    generation: 12,
    applied_generation: 11,
    up_to_date: false,
    created_at: '2026-01-19T14:40:00Z',
    updated_at: '2026-07-28T18:03:00Z',
  },
  {
    slug: 'lumen',
    display_name: 'Lumen Health',
    tenancy_domain: 'lumen.gate.example',
    console_domain: 'lumen.gate.example',
    gateway_domain: 'proxy.lumen.gate.example',
    admin_group: 'lumen-admins',
    state: 'requested',
    version: 'v0.7.0',
    transport_ca_fingerprint: null,
    generation: 1,
    applied_generation: null,
    up_to_date: false,
    created_at: '2026-07-27T08:05:00Z',
    updated_at: '2026-07-27T08:05:00Z',
  },
  {
    slug: 'brightpath',
    display_name: 'Brightpath Media',
    tenancy_domain: 'brightpath.gate.example',
    console_domain: 'brightpath.gate.example',
    gateway_domain: 'proxy.brightpath.gate.example',
    admin_group: 'brightpath-admins',
    state: 'suspended',
    version: 'v0.6.1',
    transport_ca_fingerprint: 'C7:12:9F:44:AB:30:6E:D5:81:23:BC:07:5A:E9:60:2F',
    generation: 7,
    applied_generation: 7,
    up_to_date: true,
    created_at: '2025-11-02T11:00:00Z',
    updated_at: '2026-06-14T10:20:00Z',
  },
  {
    slug: 'vector',
    display_name: 'Vector Analytics',
    tenancy_domain: 'vector.gate.example',
    console_domain: 'vector.gate.example',
    gateway_domain: 'proxy.vector.gate.example',
    admin_group: 'vector-admins',
    state: 'failed',
    version: 'v0.7.0',
    transport_ca_fingerprint: null,
    generation: 3,
    applied_generation: 2,
    up_to_date: false,
    created_at: '2026-07-25T16:22:00Z',
    updated_at: '2026-07-29T07:41:00Z',
  },
];

/**
 * The identity block on every other tenancy's card.
 *
 * A card always carries one — only a listing leaves it out — so the other
 * tenancies need one too. They are read-only wherever they appear: an operator
 * sees which provider a customer's people come through and has no route that
 * changes it.
 */
function refOf(tenant: Tenant): TenancyRef {
  return {
    slug: tenant.slug,
    display_name: tenant.display_name,
    tenancy_domain: tenant.tenancy_domain,
    console_domain: tenant.console_domain,
    gateway_domain: tenant.gateway_domain,
  };
}

const providerKind: IdentityChoice = {
  kind: 'provider',
  issuer: '',
  client_id: '',
  audience: '',
  signing_algs: [],
};

const ownKind = (issuer: string, clientId: string): IdentityChoice => ({
  kind: 'own',
  issuer,
  client_id: clientId,
  audience: '',
  signing_algs: ['RS256'],
});

function identityOf(slug: string, running: IdentityChoice, changed: string): TenancyIdentity {
  const tenant = STUB_TENANTS.find((one) => one.slug === slug) ?? STUB_TENANTS[0];
  return {
    tenancy: refOf(tenant),
    state: 'applied',
    in_flight: false,
    desired: running,
    applied: running,
    revertable: false,
    redirect_uri: `https://${tenant.tenancy_domain}/login`,
    provider_available: true,
    provider_issuer: STUB_PROVIDER_ISSUER,
    detail:
      running.kind === 'provider'
        ? "now on the provider's identity provider"
        : `now on ${running.issuer}`,
    changed_at: changed,
  };
}

/** Declared, never applied: nothing for the card to call "in use". */
function declaredOnly(slug: string, changed: string): TenancyIdentity {
  const settled = identityOf(slug, providerKind, changed);
  const pending: TenancyIdentity = {
    ...settled,
    state: 'pending',
    detail: 'declared, the cluster has not been told',
  };
  delete pending.applied;
  return pending;
}

export const STUB_OTHER_IDENTITIES: Record<string, TenancyIdentity> = {
  northwind: identityOf(
    'northwind',
    ownKind('https://sso.northwind.example', 'gdsgate-prod'),
    '2026-02-02T10:00:00Z',
  ),
  lumen: declaredOnly('lumen', '2026-07-27T08:05:00Z'),
  brightpath: identityOf('brightpath', providerKind, '2025-11-02T11:30:00Z'),
  vector: {
    ...identityOf('vector', ownKind('https://login.vector.example', 'gdsgate'), '2026-07-29T07:41:00Z'),
    state: 'rejected',
    failure: 'issuer_mismatch',
    detail: 'issuer: the provider advertises an issuer other than the one configured',
  },
};

/**
 * The declared catalogue of the tenancy whose cluster console the stubs serve.
 *
 * The fields are the server's: no serving connector and no health, because gdsgate
 * has neither, and a column filled with something plausible would be a column
 * that lies.
 */
/**
 * One boundary and one row inside it: enough for the screen to show both kinds
 * of catalogue row, the declared and the leased.
 */
export const STUB_SCOPES: Scope[] = [
  {
    prefix: 'k8s-',
    node: 'kube-conn-1',
    kinds: ['postgres', 'ssh'],
    project: 'qa',
    environment: 'test',
    labels: { origin: 'k8s-auto' },
    accept_label_keys: ['app'],
    max_live: 200,
    lease_secs: 900,
    fallback: 'parent',
    ssh_host_key: 'annotation',
    enabled: true,
    rows: 1,
  },
];

export const STUB_RESOURCES: Resource[] = [
  {
    id: 'app-prod-03',
    kind: 'ssh',
    project: 'web',
    environment: 'prod',
    labels: { tier: 'gold' },
    hostname: 'app-prod-03.internal',
  },
  {
    id: 'app-prod-04',
    kind: 'ssh',
    project: 'web',
    environment: 'prod',
    labels: { tier: 'gold' },
    hostname: 'app-prod-04.internal',
  },
  {
    id: 'orders-db',
    kind: 'postgres',
    project: 'payments',
    environment: 'prod',
    labels: { tier: 'gold' },
    parent: 'app-prod-03',
    min_approvers: 2,
    hostname: 'orders-db.internal',
    aliases: ['orders'],
    port: 5432,
  },
  {
    // Written by the connector inside `k8s-`, so it carries a lease and nobody
    // declared it.
    id: 'k8s-orders-orders-db',
    kind: 'postgres',
    project: 'qa',
    environment: 'test',
    labels: { origin: 'k8s-auto', app: 'orders' },
    hostname: 'k8s-orders-orders-db',
    port: 5432,
    scope: 'k8s-',
    lease_expires_at: Math.floor(Date.now() / 1000) + 600,
  },
  {
    id: 'analytics-db',
    kind: 'mysql',
    project: 'data',
    environment: 'staging',
    labels: {},
    hostname: 'analytics.internal',
  },
  {
    id: 'prod-cluster',
    kind: 'kubernetes',
    project: 'platform',
    environment: 'prod',
    labels: { tier: 'gold' },
    hostname: 'k8s.internal',
    port: 6443,
  },
  {
    id: 'docs-mcp',
    kind: 'mcp',
    project: 'default',
    environment: 'dev',
    labels: {},
    hostname: 'mcp.internal',
    port: 8080,
  },
  // An application behind the gateway, carrying the domain it answers on and no
  // port — 80 is the port of its kind, and a row that declared it would be
  // saying what the kind already says.
  {
    id: 'grafana',
    kind: 'web',
    project: 'web',
    environment: 'prod',
    labels: { tier: 'gold' },
    hostname: 'grafana',
    native_name: 'grafana.acme.example',
  },
  // The way out, which is the other shape of the same lane: no name of its own
  // to answer as, and a port it must declare because it is written into
  // $HTTP_PROXY rather than dialled by name.
  {
    id: 'egress',
    kind: 'web-egress',
    project: 'default',
    environment: 'prod',
    labels: {},
    hostname: 'egress',
    port: 3128,
  },
];

// ---------------------------------------------------------------------------
// The last application: its certificate and its anonymous surface
// ---------------------------------------------------------------------------

const DAY = 24 * HOUR;

function inDays(days: number): string {
  return new Date(Date.now() + days * DAY).toISOString();
}

/**
 * The two names every tenancy is published under, worked out the way the server
 * works them out — from the tenancy domain, in one place.
 */
function certificateNames(domain: string): string[] {
  return [domain, `proxy.${domain}`];
}

/**
 * What each tenancy's certificate is, and what it is supposed to be.
 *
 * Five states, and each is one the screen has to draw differently:
 *
 * - **acme** — `kept`: the authority was not asked at all, because the pair is
 *   valid and covers both names. The ordinary and the wanted outcome;
 * - **northwind** — a pair somebody else installed, covering the console name
 *   and not the gateway one. The sets diverge, which is a finding with no other
 *   symptom, and the authority behind it is not this control plane's to vouch
 *   for;
 * - **lumen** — `untended`: the names are outside the only zone this deployment
 *   can write in, so nothing is asked of the authority. No certificate, and
 *   therefore no registration relay;
 * - **brightpath** — obtained from the **test** authority. Real TLS, and refused
 *   by anybody who was not told about that root;
 * - **vector** — a failed attempt with the next one scheduled. Three failures in
 *   a row, and nothing is expiring while the wait runs.
 */
export const STUB_CERTIFICATES: Record<string, CertificateReport> = {
  acme: {
    outcome: 'kept',
    wanted: certificateNames(STUB_TENANCY_DOMAIN),
    installed: certificateNames(STUB_TENANCY_DOMAIN),
    missing: [],
    not_after: inDays(52),
    renew_at: inDays(22),
    authority: 'live',
    publicly_trusted: true,
    attempts: 0,
    retry_after: null,
    detail:
      'the pair on the disk is valid and covers every name, so the authority was not asked for anything',
  },
  northwind: {
    outcome: 'foreign',
    wanted: certificateNames('northwind.gate.example'),
    installed: ['northwind.gate.example'],
    missing: ['proxy.northwind.gate.example'],
    not_after: inDays(120),
    renew_at: inDays(90),
    authority: null,
    publicly_trusted: null,
    attempts: 0,
    retry_after: null,
    detail:
      'there is a certificate here that this control plane neither installed nor renews, so it is left exactly as it is',
  },
  lumen: {
    outcome: 'untended',
    wanted: certificateNames('lumen.gate.example'),
    installed: [],
    missing: [],
    not_after: null,
    renew_at: null,
    authority: null,
    publicly_trusted: null,
    attempts: 0,
    retry_after: null,
    detail:
      '`lumen.gate.example` is not inside `gate.stagequant.tech`, the only zone this deployment can write a record in, so nothing is asked of the authority: install a pair from whoever holds that name',
  },
  brightpath: {
    outcome: 'obtained',
    wanted: certificateNames('brightpath.gate.example'),
    installed: certificateNames('brightpath.gate.example'),
    missing: [],
    not_after: inDays(89),
    renew_at: inDays(59),
    authority: 'staging',
    publicly_trusted: false,
    attempts: 0,
    retry_after: null,
    detail: 'obtained and installed; both listeners were pointed at it',
  },
  vector: {
    outcome: 'failed',
    wanted: certificateNames('vector.gate.example'),
    installed: [],
    missing: [],
    not_after: null,
    renew_at: null,
    authority: null,
    publicly_trusted: null,
    attempts: 3,
    retry_after: new Date(Date.now() + 2 * HOUR).toISOString(),
    detail:
      'the challenge for `proxy.vector.gate.example` was not accepted: the TXT record was published and the authority still read NXDOMAIN',
  },
};

/** The numbers every tenancy is rendered with. The server's, taken as they come. */
const BUDGET_NUMBERS = { rate_per_ip: 10, burst: 60 } as const;

const NUMBERS =
  '10 calls/s per source address, burst 60, on the two RPCs that answer before a caller is anybody';

/**
 * The server's own sentence for each standing.
 *
 * Each says what it can prove and never more, exactly as the server's does: read
 * means the process holding the gateway now is not the one that held it when the
 * number was written, and unread means it is the very same one.
 */
const BUDGET_DETAIL: Record<SettingsState, string> = {
  in_force: `the gateway has started since it was written and is holding anonymous callers to it: ${NUMBERS}`,
  not_read_yet: `written and not read since: ${NUMBERS}. The gateway reads \`[proxy]\` once, when it starts, and the process running it now is the one that was already running then — until then it answers every address as often as it asks, which is what a file carrying no budget means. Nothing here restarts it: recreating the gateway drops every live session and every connector tunnel, so the number takes effect at the tenancy's next start`,
  at_the_next_start: `nothing is running this tenancy's gateway, so nobody is being held to anything; the file its next start reads carries ${NUMBERS}`,
  not_known: `this control plane found the budget already in the gateway's file rather than writing it, so it has no witness of when it got there: the file carries ${NUMBERS}, and whether the gateway running now has read it is settled by that gateway starting again rather than by this pass`,
  not_written: `the certificate step failed before any configuration was rendered, so what the tenancy's gateway is under is what the last pass left it under`,
};

/** The two values of the gateway's the budget is made of, in gdsgate's spelling. */
const BUDGET_SETTINGS = [
  '[proxy].public_anonymous_burst',
  '[proxy].public_anonymous_rate_per_ip',
] as const;

/**
 * Where the values rendered for each role stand, and the sentence for each.
 *
 * `auth` answers `in_force` while the gateway answers `not_read_yet`, which is
 * the shape worth demonstrating: a role answers for itself, and a value of
 * Auth's waiting for a start would not spoil the answer about the gateway's.
 */
const SETTINGS_DETAIL: Record<SettingsState, (role: string) => string> = {
  in_force: (role) =>
    `the ${role} started after the last value written for it, so what it is running is what this control plane renders`,
  not_read_yet: (role) =>
    `the ${role} running now cannot have read 2 of the values rendered for it — ${BUDGET_SETTINGS.join(', ')}. A role reads its configuration once, when it starts, and this process was already running when it was written. Nothing here restarts it — recreating a role drops every live session and every connector tunnel — so it takes effect at the tenancy's next start`,
  at_the_next_start: (role) =>
    `nothing is running the ${role}, so nothing is being held to anything; whatever starts next reads what is rendered`,
  not_known: (role) =>
    `this control plane found every value rendered for the ${role} already in place rather than writing it, so what the process running now has read is settled by that process starting again rather than by this pass`,
  not_written: (role) =>
    `the configuration step did not run on this pass, so what the tenancy's ${role} is running is what the last pass left it running`,
};

function budgetIn(standing: SettingsState): AnonymousBudget {
  return { ...BUDGET_NUMBERS, state: standing, detail: BUDGET_DETAIL[standing] };
}

function roleIn(role: string, standing: SettingsState, unread: string[] = []): RoleReading {
  return { role, state: standing, unread, detail: SETTINGS_DETAIL[standing](role) };
}

/**
 * Where each tenancy of this demonstration stands on what has been rendered for
 * it — **five tenancies, five standings**, because each of the five is drawn
 * differently and only one of them is a finding.
 *
 * This is the tenancy's own property and not the report's: it changes when a
 * gateway starts, which is not something applying a tenancy does. That is the
 * whole of the fix it demonstrates — pressing "Apply" against brightpath a
 * second time leaves it exactly where it was, because nothing restarted its
 * gateway.
 */
export const STUB_STANDINGS: Record<string, SettingsState> = {
  acme: 'in_force',
  // Standing before this control plane kept a witness of the write: there is
  // nothing to compare against, and the honest answer is that it cannot say.
  northwind: 'not_known',
  // Out of the zone, and its gateway is not up at all.
  lumen: 'at_the_next_start',
  // Stood up long before these keys existed. Its gateway is the very process
  // that was running when the numbers were written, so it is proved not to have
  // read them — and will not until somebody restarts it, which nothing here does.
  brightpath: 'not_read_yet',
  // Its pass stopped at the certificate, so nothing was rendered at all.
  vector: 'not_written',
};

/**
 * What a tenancy declared in this demonstration gets on its first application.
 *
 * From the **test** authority, because that is what a deployment issues until
 * somebody writes the one line that turns the live one on. A demonstration that
 * quietly produced publicly trusted certificates would be showing a deployment
 * nobody has.
 */
export function stubFirstCertificate(domain: string): CertificateReport {
  return {
    outcome: 'obtained',
    wanted: certificateNames(domain),
    installed: certificateNames(domain),
    missing: [],
    not_after: inDays(89),
    renew_at: inDays(59),
    authority: 'staging',
    publicly_trusted: false,
    attempts: 0,
    retry_after: null,
    detail: 'obtained and installed; both listeners were pointed at it',
  };
}

/** The budget as one standing reports it, with the sentence that belongs to it. */
export function stubBudgetIn(standing: SettingsState): AnonymousBudget {
  return budgetIn(standing);
}

/**
 * What the roles report at one standing — always both of them, as the server
 * always sends both.
 *
 * The gateway's standing is the tenancy's standing; Auth answers `in_force`
 * wherever the gateway is proved not to have read something, because the two are
 * separate processes and the server judges them separately.
 */
export function stubSettingsIn(standing: SettingsState): RenderedSettings {
  if (standing === 'not_read_yet') {
    return {
      roles: [roleIn('authority', 'in_force'), roleIn('proxy', 'not_read_yet', [...BUDGET_SETTINGS])],
    };
  }
  return { roles: [roleIn('authority', standing), roleIn('proxy', standing)] };
}

/**
 * The steps of each stored report.
 *
 * The certificate step carries **0**, and that is the server's number rather
 * than a gap: the pair has to be on the disk before the configuration that names
 * it is rendered, which is contract step 3, and gdsgate's provisioning contract
 * has no step of its own for something that happens before all of it.
 */
const CERTIFICATE_STEP: Record<string, string> = {
  kept: 'unchanged',
  obtained: 'applied',
  waiting: 'failed',
  failed: 'failed',
  foreign: 'unchanged',
  // Nothing to do and a reason for it, which is what `skipped` is for.
  untended: 'skipped',
};

function certificateStepOutcome(certificate: CertificateReport): string {
  // A published name the pair does not carry fails the step whatever the pair
  // is, including one somebody else installed.
  if (certificate.missing.length > 0) return 'failed';
  return CERTIFICATE_STEP[certificate.outcome] ?? 'failed';
}

/**
 * @param written whether the pass got as far as rendering the configuration. A
 * pass that did not is the one that reports `not_written`, and its step has to
 * say so: a table claiming six files were rendered beside a block saying nothing
 * was written would be the card contradicting itself.
 */
export function stubReportSteps(
  certificate: CertificateReport,
  accepted: boolean,
  written: boolean,
): ReconcileStep[] {
  return [
    { contract_step: 1, step: 'storage', outcome: 'unchanged', detail: 'credentials already held' },
    {
      contract_step: 0,
      step: 'certificate',
      outcome: certificateStepOutcome(certificate),
      detail: certificate.detail,
    },
    written
      ? { contract_step: 3, step: 'files', outcome: 'applied', detail: 'rendered 6 files' }
      : {
          contract_step: 3,
          step: 'files',
          outcome: 'skipped',
          detail:
            'nothing was rendered: the pair the gateway configuration names is not on the disk',
        },
    {
      contract_step: 13,
      step: 'doctor',
      outcome: accepted ? 'applied' : 'failed',
      detail: accepted
        ? 'exit 0 on every role'
        : 'the tenancy was not handed over; the lines above say what stopped it',
    },
  ];
}

/**
 * The report on each tenancy's card, as the last application left it.
 *
 * A tenancy declared in this demonstration has none at all, which is the other
 * case the card has to draw: nothing has been applied, so nothing has looked at
 * its certificate.
 */
export const STUB_REPORTS: Record<string, ReconcileReport> = storedReports();

function storedReports(): Record<string, ReconcileReport> {
  const reports: Record<string, ReconcileReport> = {};
  for (const tenant of STUB_TENANTS) {
    const certificate = STUB_CERTIFICATES[tenant.slug];
    const standing = STUB_STANDINGS[tenant.slug];
    if (certificate === undefined || standing === undefined) continue;

    // A tenancy is handed over only by a pass that was accepted, and only such a
    // pass moves its applied generation. So the two agree here rather than being
    // set by hand in two places.
    const accepted = tenant.applied_generation === tenant.generation;
    reports[tenant.slug] = {
      tenant: tenant.slug,
      generation: tenant.generation,
      actor: STUB_ACCOUNTS.admin,
      started_at: tenant.updated_at,
      finished_at: tenant.updated_at,
      steps: stubReportSteps(certificate, accepted, standing !== 'not_written'),
      outcome: accepted ? 'accepted' : 'not_accepted',
      transport_ca_fingerprint: tenant.transport_ca_fingerprint,
      certificate,
      anonymous_budget: budgetIn(standing),
      rendered_settings: stubSettingsIn(standing),
    };
  }
  return reports;
}

/**
 * The registry rows behind the connector figure.
 *
 * One node named itself nothing, and it is here for that reason: a name a node
 * did not give is a case the screen has to draw without inventing one. The
 * others carry names that look like places, which is what makes the "claimed by
 * the node" mark worth showing — nothing checked any of them.
 *
 * `as_of` is filled in when the figures are read, not here: the whole point of
 * that field is that it is the moment the registry was read.
 */
export const STUB_AGENTS: Omit<AgentUsage, 'as_of'> = {
  active: 3,
  revoked: 1,
  connectors: [
    { id: 'n-7', claimed_name: 'warehouse rack 4', enrolled_at: '2026-07-02T11:00:00Z' },
    { id: 'n-9', claimed_name: 'db-bastion', enrolled_at: '2026-07-11T08:24:00Z' },
    { id: 'n-12', enrolled_at: '2026-07-19T15:40:00Z' },
  ],
};

/** The kinds in the cluster's own catalogue — the served one, not the declared one. */
export const STUB_RESOURCE_USAGE: Omit<ResourceUsage, 'as_of'> = {
  count: 6,
  by_kind: { kubernetes: 1, mcp: 1, mysql: 1, postgres: 1, ssh: 2 },
};

/**
 * Sessions as an export that could be read would report them.
 *
 * `recorded_bytes` is deliberately far smaller than the session count would
 * suggest, and `by_kind` says why: gdsgate records shell casts, so the database
 * and Kubernetes sessions in here closed at zero bytes however much traffic
 * crossed them. A screen that called this field "volume" would be wrong by the
 * whole of that difference.
 */
export const STUB_SESSIONS_USAGE: SessionUsage = {
  count: 340,
  closed: 335,
  unclosed: 5,
  duration_secs: 481203,
  recorded_bytes: 91234567,
  closed_without_open: 0,
  by_kind: { kubernetes: 12, postgres: 40, ssh: 288 },
};

/** More rows than people, which is the normal state of a cluster that has run a month. */
export const STUB_PEOPLE_USAGE: PeopleUsage = { count: 17, rows: 96 };

// ---------------------------------------------------------------------------
// Versions, and rolling one out
// ---------------------------------------------------------------------------

/**
 * The sentences the control plane uses for a version it cannot read, copied
 * from it rather than paraphrased.
 *
 * The first two are permanent and say why: there is nowhere for those versions
 * to come from. The third is a reading that was taken and thrown away, which is
 * a different thing and has to read as one — every tenancy's gateway is
 * published on one host address in a deployment, so whoever answers is whichever
 * tenancy is up, and a board that took the answer on trust would show one
 * customer another customer's version.
 */
export const STUB_CONNECTOR_UNTOLD =
  'nothing a connector sends its cluster carries a version, and the registry has no column for one, ' +
  'so the only component that drifts on somebody else’s schedule is the one that cannot be read';

export const STUB_CLIENT_UNTOLD =
  'a client is somebody’s own machine: it enrols no identity, appears in no registry, and nothing ' +
  'it sends carries a version';

export const STUB_NOT_READ =
  'this tenant’s versions have not been read since the control plane began recording them; the ' +
  'next reconcile records them';

const STUB_WRONG_ORIGIN =
  'the gateway answering at 127.0.0.1:8443 publishes `https://proxy.lumen.gate.example`, and this ' +
  'tenancy’s is `https://proxy.vector.gate.example`. Every tenant’s gateway is published on one ' +
  'host address here, so what answered belongs to another tenancy and its version says nothing ' +
  'about this one';

/** How a version for each component is come by, and therefore what it is worth. */
export const STUB_READ_BY = {
  proxy:
    'asked over the wire: the answer is compiled into the process that is serving, so it is what ' +
    'is running and not what is installed',
  auth:
    'its own image, run once: the build the tenant’s compose file names. The running process is ' +
    'on it only if it has been restarted since that file was written, which an accepted reconcile ' +
    'does',
} as const;

/** A component that answered, and one that did not. */
export function stubRead(component: string, version: string): ComponentVersion {
  const readBy = component === 'proxy' ? STUB_READ_BY.proxy : STUB_READ_BY.auth;
  return { component, version, read_by: readBy };
}

export function stubUnread(component: string, reason: string): ComponentVersion {
  const readBy = component === 'proxy' ? STUB_READ_BY.proxy : STUB_READ_BY.auth;
  return { component, reason, read_by: readBy };
}

/** The two that cannot be read at all: no version, and no way to ask for one. */
const CLIENT: ComponentVersion = { component: 'client', reason: STUB_CLIENT_UNTOLD };
const AGENT: ComponentVersion = { component: 'connector', reason: STUB_CONNECTOR_UNTOLD };

function readings(proxy: ComponentVersion, auth: ComponentVersion): ComponentVersion[] {
  return [CLIENT, proxy, auth, AGENT];
}

/**
 * What each tenancy's components were last seen running.
 *
 * Four cases worth seeing, and each is a different thing on screen:
 *
 * - **acme** — both read, on one build.
 * - **northwind** — both read, and a named break lies between them: its gateway
 *   is on 0.6.1 and its control plane on 0.7.0.
 * - **lumen** — never read at all; its cluster has not been built.
 * - **vector** — a reading that was taken and discarded, because the address
 *   answered for another tenancy. It is not the same as "cannot be read": it can
 *   be, and next time it may be.
 *
 * The client and the connector are unreadable on every one of them, which is the
 * state of the product rather than of this demonstration.
 */
export const STUB_VERSIONS: Record<string, ComponentVersion[]> = {
  acme: readings(stubRead('proxy', '0.7.0'), stubRead('authority', '0.7.0')),
  northwind: readings(stubRead('proxy', '0.6.1'), stubRead('authority', '0.7.0')),
  lumen: readings(
    stubUnread('proxy', STUB_NOT_READ),
    stubUnread('authority', STUB_NOT_READ),
  ),
  brightpath: readings(stubRead('proxy', '0.6.1'), stubRead('authority', '0.6.1')),
  vector: readings(stubUnread('proxy', STUB_WRONG_ORIGIN), stubRead('authority', '0.7.0')),
};

/** When those readings were taken. A tenancy never read has none. */
export const STUB_OBSERVED_AT: Record<string, string> = {
  acme: '2026-07-29T09:12:00Z',
  northwind: '2026-07-28T18:03:00Z',
  brightpath: '2026-06-14T10:20:00Z',
  vector: '2026-07-29T07:41:00Z',
};

/**
 * Which wave each tenancy moves in.
 *
 * Only Acme is a canary. Everything else is in the rest wave because that is
 * where a tenancy is until somebody decides otherwise — the cautious default is
 * the one that matters, since a tenancy nobody thought about must not be the
 * one that finds out first.
 */
export const STUB_WAVES: Record<string, string> = { acme: 'canary' };

/**
 * The tenancy whose move fails on purpose, and what the failure says.
 *
 * A wave where everything went well is the easy half. This is the other one:
 * the wave cannot be signed off, the refusal names this tenancy, and that
 * refusal is what somebody has to read.
 */
export const STUB_MOVE_FAILS = 'vector';

export const STUB_MOVE_FAILURE =
  'applied and not accepted: doctor exited 2 on auth: the OIDC issuer did not answer';

/**
 * Why sessions and people arrive missing today.
 *
 * The control plane counts them out of the tenancy's exported audit chain, and
 * in the deployment as it stands that file lives inside the tenancy's own Auth
 * container, on a path no host process knows. So the answer names the figure and
 * the reason, and does not put a zero where a number could not be worked out.
 */
export const STUB_EXPORT_UNREADABLE =
  'the audit export at /var/lib/gdsgate/acme/audit-export.jsonl could not be opened: no such file or directory';

// ---------------------------------------------------------------------------
// Our own trail
// ---------------------------------------------------------------------------

/**
 * What this control plane is recorded as having done, per tenancy.
 *
 * The vocabulary is the server's, copied rather than invented: `tenant.declare`,
 * `tenant.reconcile`, `catalog.put`, `identity.choose`, `rollout.wave`,
 * `rollout.move`, and the outcomes those are written with. A demonstration that
 * made up its own words here would be teaching an operator to read something
 * that never appears.
 *
 * Newest first, as the answer is. The stub adds to the front of it whenever
 * something is done in this tab, which is the point of having it at all: the
 * line somebody just caused is the one they look for.
 */
export interface StubTrailEntry extends TrailEntry {
  /** Which tenancy's trail it belongs to. The server filters by it in the query. */
  slug: string;
}

export const STUB_TRAIL: StubTrailEntry[] = [
  {
    slug: 'acme',
    seq: 41,
    at: '2026-07-29T09:12:00Z',
    actor: STUB_ACCOUNTS.admin,
    action: 'tenant.reconcile',
    outcome: 'accepted',
    detail: 'generation=4',
  },
  {
    slug: 'acme',
    seq: 38,
    at: '2026-07-28T16:40:00Z',
    actor: 'kate@acme.example',
    action: 'catalog.put',
    outcome: 'success',
    detail: 'resource=orders-db kind=postgres',
  },
  {
    slug: 'acme',
    seq: 33,
    at: '2026-07-27T11:02:00Z',
    actor: 'kate@acme.example',
    action: 'identity.choose',
    outcome: 'success',
    detail: 'kind=provider issuer=https://id.provider.example',
  },
  {
    slug: 'acme',
    seq: 12,
    at: '2026-07-26T09:12:00Z',
    actor: STUB_ACCOUNTS.admin,
    action: 'tenant.declare',
    outcome: 'success',
    detail:
      'slug=acme tenancy=acme.gate.example gateway=proxy.acme.gate.example identity=provider version=v0.7.0',
  },
  {
    slug: 'northwind',
    seq: 40,
    at: '2026-07-28T18:03:00Z',
    actor: STUB_ACCOUNTS.admin,
    action: 'tenant.reconcile',
    outcome: 'accepted',
    detail: 'generation=11',
  },
  {
    slug: 'northwind',
    seq: 9,
    at: '2026-01-19T14:40:00Z',
    actor: STUB_ACCOUNTS.admin,
    action: 'tenant.declare',
    outcome: 'success',
    detail:
      'slug=northwind tenancy=northwind.gate.example gateway=proxy.northwind.gate.example identity=provider version=v0.6.1',
  },
  {
    slug: 'vector',
    seq: 39,
    at: '2026-07-29T07:41:00Z',
    actor: STUB_ACCOUNTS.admin,
    action: 'tenant.reconcile',
    outcome: 'not_accepted',
    detail: 'generation=3',
  },
  {
    slug: 'vector',
    seq: 11,
    at: '2026-07-25T16:22:00Z',
    actor: STUB_ACCOUNTS.admin,
    action: 'tenant.declare',
    outcome: 'success',
    detail:
      'slug=vector tenancy=vector.gate.example gateway=proxy.vector.gate.example identity=own version=v0.7.0',
  },
  {
    slug: 'brightpath',
    seq: 8,
    at: '2026-06-14T10:20:00Z',
    actor: STUB_ACCOUNTS.admin,
    action: 'tenant.state',
    outcome: 'success',
    detail: 'state=suspended',
  },
  {
    slug: 'lumen',
    seq: 10,
    at: '2026-07-27T08:05:00Z',
    actor: STUB_ACCOUNTS.admin,
    action: 'tenant.declare',
    outcome: 'success',
    detail:
      'slug=lumen tenancy=lumen.gate.example gateway=proxy.lumen.gate.example identity=provider version=v0.7.0',
  },
];
